import * as React from 'react';
import type { SvgProps } from 'react-native-svg';
import { getIcon, IconStyle, IconName } from './generated';
import type { IconProps as BaseIconProps } from './generated';

export interface SolarIconProps extends Omit<SvgProps, 'color'> {
  /**
   * The icon name (e.g., "Home", "ArrowDown")
   */
  name: IconName | (string & {});

  /**
   * Icon style variant
   * @default 'linear'
   */
  type?: IconStyle;

  /**
   * Shorthand for width and height
   * @default 24
   */
  size?: number;

  /**
   * Icon color (shorthand for primaryColor)
   * @default '#000000'
   */
  color?: string;

  /**
   * Primary icon color
   */
  primaryColor?: string;

  /**
   * Secondary color for duotone icons
   */
  secondaryColor?: string;
}

/**
 * SolarIcon - A unified icon component for Solar Icons
 *
 * @example
 * ```tsx
 * // Basic usage
 * <SolarIcon name="Home" />
 *
 * // With style type
 * <SolarIcon name="Home" type="bold" />
 *
 * // With size and color
 * <SolarIcon name="Heart" type="bold" size={32} color="#ff0000" />
 *
 * // Duotone with two colors
 * <SolarIcon
 *   name="Home"
 *   type="bold-duotone"
 *   primaryColor="#000"
 *   secondaryColor="#999"
 * />
 * ```
 */
export const SolarIcon: React.FC<SolarIconProps> = ({
  name,
  type = 'linear',
  size = 24,
  color = '#000000',
  primaryColor,
  secondaryColor,
  ...props
}) => {
  const IconComponent = getIcon(type, name);

  if (!IconComponent) {
    if (__DEV__) {
      console.warn(
        `[SolarIcon] Icon "${name}" not found in style "${type}". ` +
          `Check the icon name and style, or see the icon manifest for available icons.`
      );
    }
    return null;
  }

  const iconProps: BaseIconProps = {
    size,
    color,
    primaryColor,
    secondaryColor,
    ...props,
  };

  return <IconComponent {...iconProps} />;
};

// Re-export everything from generated
export * from './generated';
export type { IconStyle, IconName } from './generated';

// Declare __DEV__ for TypeScript
declare const __DEV__: boolean;
